/**
 * SMS segmentation engine (PRO-0160 §6 / D5 — extracted from PRO-0130's
 * `validateTemplateContent`). PURE, deterministic, side-effect-free (no DB, no
 * logging) — exactly like `validateTemplateContent`, `resolveMergeFields` and
 * `dedupeRecipients` — so the GSM-7/UCS-2 maths is the SINGLE source of truth
 * shared by authoring (template publish gate, draft-field validation) AND the
 * compose preview over the RESOLVED body.
 *
 * Why this lives here (D5): the resolved body length/encoding can differ from
 * the template body once `{{tokens}}` become names, so the preview must
 * recompute the segment/char count on the resolved body — but never by a second
 * implementation. `validateTemplateContent` imports `calcSmsSegments` from here;
 * the maths is byte-identical to the prior module-private `calcSms`.
 *
 * SEC-8: this module never sees a name or persists/log anything — it computes
 * counts over a string the caller supplies and returns counts/encoding only.
 */
import type { SmsEncoding } from '../types/template';

/** A single GSM-7 SMS holds 160 septets; concatenated parts hold 153 (7-bit UDH). */
export const SMS_GSM7_SINGLE = 160;
export const SMS_GSM7_CONCAT = 153;
/** A single UCS-2 SMS holds 70 UTF-16 units; concatenated parts hold 67. */
export const SMS_UCS2_SINGLE = 70;
export const SMS_UCS2_CONCAT = 67;

/** GSM 03.38 basic set (1 septet each). Space is included explicitly. */
const GSM7_BASIC = new Set(
  Array.from(
    '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà',
  ),
);

/** GSM 03.38 extension set (2 septets each — escape-prefixed). */
const GSM7_EXTENSION = new Set(Array.from('^{}\\[~]|€'));

/** True when every character is GSM-7 encodable (basic or extension set). */
export function isGsm7(text: string): boolean {
  for (const ch of text) {
    if (!GSM7_BASIC.has(ch) && !GSM7_EXTENSION.has(ch)) {
      return false;
    }
  }
  return true;
}

/** GSM-7 septet count (extension-set characters cost 2 septets each). */
function gsm7Septets(text: string): number {
  let count = 0;
  for (const ch of text) {
    count += GSM7_EXTENSION.has(ch) ? 2 : 1;
  }
  return count;
}

/** UTF-16 code units (the UCS-2 unit; a surrogate pair counts as 2). */
function ucs2Units(text: string): number {
  return text.length;
}

export interface SmsCalc {
  readonly segments: number;
  readonly encoding: SmsEncoding;
  /** Encoding-aware character count: GSM-7 septets or UCS-2 units (for display). */
  readonly charCount: number;
}

/**
 * Compute the GSM-7/UCS-2 encoding, segment count and encoding-aware character
 * count for a body. Identical segment/encoding maths to the prior private
 * `calcSms` (PRO-0130); `charCount` is additive for the preview display. Always
 * at least one segment (an empty body is one zero-length segment).
 */
export function calcSmsSegments(body: string): SmsCalc {
  if (isGsm7(body)) {
    const septets = gsm7Septets(body);
    const segments = septets <= SMS_GSM7_SINGLE ? 1 : Math.ceil(septets / SMS_GSM7_CONCAT);
    return { segments: Math.max(segments, 1), encoding: 'GSM-7', charCount: septets };
  }
  const units = ucs2Units(body);
  const segments = units <= SMS_UCS2_SINGLE ? 1 : Math.ceil(units / SMS_UCS2_CONCAT);
  return { segments: Math.max(segments, 1), encoding: 'UCS-2', charCount: units };
}
